import Context from 'managers/Context';
import { getActiveCrudName } from 'utils/crud';

/**
 * Helper function to get the label for a field from the schema
 * @param {string} field - The field configuration identifier
 * @param {Array} schema - Array of schema objects containing field definitions
 * @returns {string} The field label if found, otherwise returns the field identifier
 */
const getFieldLabel = (field, schema) => {
    if (!field || typeof field !== 'string') {
        return '';
    }

    if (!Array.isArray(schema)) {
        return field;
    }

    let fieldLabel;
    schema.forEach((s) => {
        if (s?.fields && Array.isArray(s.fields)) {
            const foundField = s.fields.find((f) => f.fieldConfiguration === field);
            if (foundField) {
                fieldLabel = foundField.label;
            }
        }
    });
    return fieldLabel || field;
};

/**
 * Gets all dependencies for a field and returns them as an object with field IDs as keys and labels as values
 * Dependencies are collected from three sources:
 * 1. dependencyMap - Direct dependencies defined in lowercase
 * 2. dynamicMap - Dynamic dependencies with field-specific configurations
 * 3. smartFieldsDependencyMap - Dependencies from smart field configurations
 *
 * @param {Object} params - Function parameters
 * @param {Object} params.field - The field object containing fieldConfiguration
 * @param {Array} params.schema - Array of schema objects containing field definitions
 * @returns {Object} An object where keys are dependency field IDs and values are their corresponding labels
 */
export default ({ field, schema }) => {
    // Input validation
    if (!field || typeof field !== 'object' || !field.fieldConfiguration) {
        return {};
    }

    if (!Array.isArray(schema)) {
        return {};
    }

    const { fieldConfiguration } = field;
    const state = Context.store.getState();

    if (!state) {
        return {};
    }

    const activeCrudName = getActiveCrudName(state);
    if (!activeCrudName) {
        return {};
    }

    const {
        dependencyMap = {},
        dynamicMap = {},
        smartFieldsDependencyMap = {},
    } = state.entityCrud?.[activeCrudName] || {};

    const dependencies = [];

    // Safely collect dependencies from different sources
    try {
        if (dependencyMap[fieldConfiguration.toLowerCase()]) {
            dependencies.push(...dependencyMap[fieldConfiguration.toLowerCase()]);
        }

        if (dynamicMap[fieldConfiguration]) {
            const dynamicKeys = Object.keys(dynamicMap[fieldConfiguration]);
            if (Array.isArray(dynamicKeys)) {
                dependencies.push(...dynamicKeys);
            }
        }

        if (smartFieldsDependencyMap[fieldConfiguration]) {
            dependencies.push(...smartFieldsDependencyMap[fieldConfiguration]);
        }
    } catch (error) {
        console.error('Error collecting dependencies:', error);
        return {};
    }

    // Remove duplicates and transform to object with id as key and label as value
    try {
        return [...new Set(dependencies)].reduce((obj, item) => {
            if (typeof item === 'string') {
                obj[item] = getFieldLabel(item, schema);
            }
            return obj;
        }, {});
    } catch (error) {
        console.error('Error processing dependencies:', error);
        return {};
    }
};
